/*!
 * Floppy, uses Dexie (V. 3.0.1), A Minimalistic Wrapper for IndexedDB
 * Authors: ['Jorge Brunal Perez (aka Diniremix)', 'Duverney Ortiz']
 * Version: 2020-11-05
 * https://git.sitimapa.co/serviforms/serviforms-admin
 * Released under the MIT License.
 */

import Dexie from "dexie";
import DateUtils from "@/helpers/DateUtils.js";
import router from "@/router";

export default {
	db: null,

	init({dbName, versionNumber, schema}) {
		this.db = new Dexie(dbName);
		this.db.version(versionNumber).stores(schema);
		this.db.open().catch(function (e) {
			console.error("========================================================");
			console.error("IndexedDB cannot be used in this browser!.");
			console.error("Floppy cannot use IndexedDB.");
			console.error("More information at: https://caniuse.com/#feat=indexeddb");
			console.error("IndexedDB", e.name, e.message);
			console.error("========================================================");
		});
	},

	open() {
		return new Promise((resolve, reject) => {
			try {
				resolve(this.db.open());
			} catch (error) {
				reject(error);
			}
		});
	},

	close() {
		this.db.close();
	},

	isOpen() {
		return this.db.isOpen();
	},

	getAll(table) {
		const myTable = this.db.table(table);
		return myTable.toArray();
	},

	getKey(table, key) {
		const myTable = this.db.table(table);
		return myTable.get(key);
	},

	generateRandomKey() {
		return Math.random().toString(36).substring(2, 34) + Math.random().toString(36).substring(2, 34);
	},

	generateId() {
		const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
		let autoId = "";
		const maxChar = 20;
		// Generar el ID aleatorio
		for (let i = 0; i < maxChar; i++) {
			autoId += chars.charAt(Math.floor(Math.random() * chars.length));
		}
		// Validar si el ID tiene la longitud correcta, lanzar un error si no es válido
		if (autoId.length !== maxChar) {
			throw new Error(`Invalid auto ID: ${autoId}`);
		}
		return autoId;
	},

	save(table, data, key = null) {
		if (!key) key = this.generateRandomKey();
		const myTable = this.db.table(table);
		let copyData = Object.assign({}, data, {
			key: key,
			date_create: DateUtils.timeStamp(),
		});
		return myTable.add(copyData, key);
	},

	put(table, data, key) {
		const myTable = this.db.table(table);
		return myTable.put(data, key);
	},

	update(table, data, key) {
		const myTable = this.db.table(table);
		return myTable.update(key, data);
	},

	deleteKey(table, key) {
		const myTable = this.db.table(table);
		return myTable.delete(key);
	},

	count(table) {
		const myTable = this.db.table(table);
		return myTable.count();
	},

	limit(table, count) {
		const myTable = this.db.table(table);
		return myTable.limit(count).toArray();
	},

	first(table, key) {
		const myTable = this.db.table(table);
		return myTable.toCollection().first();
	},

	last(table, key) {
		const myTable = this.db.table(table);
		return myTable.toCollection().last();
	},

	getTableInstance(table) {
		const myTable = this.db.table(table);
		return new Promise((resolve, reject) => {
			if (myTable) {
				resolve(myTable);
			} else {
				reject(myTable);
			}
		});
	},

	getDBInstance() {
		return Promise.resolve(this.db);
	},

	schema() {
		return new Promise((resolve, reject) => {
			if (this.db.isOpen()) {
				const currentSchema = this.db.tables.map(({name, schema}) => {
					return {name, schema};
				});
				resolve(currentSchema);
			} else {
				reject("DatabaseClosedError: Database has been closed");
			}
		});
	},

	truncate(table, confirm) {
		const myTable = this.db.table(table);
		return myTable.clear();
	},

	//Giovanni Rosas
	giovanni(dbname) {
		this.db.close();
		// this.db.delete()
		console.warn("Giovanni Rosas has deleted the database!: ¯\\_(ツ)_/¯");
		return Dexie.delete(dbname);
	},

	deleteDB(dbname, confirm) {
		return new Promise((resolve, reject) => {
			if (confirm !== null && confirm !== undefined) {
				if (confirm === "Yes!") {
					this.db.close();
					// this.db.delete()
					resolve(Dexie.delete(dbname));
				}
				reject("DatabaseError: the database cannot be deleted, the order must be: Yes!");
			}
			reject("DatabaseError: the database cannot be deleted, confirm your order first!");
		});
	},

	dbname() {
		return this.db.name;
	},

	version() {
		return this.db.verno;
	},

	async filter_(table, options) {
		const resultData = [];
		// Create a mapping for common operations
		const operations = {
			"===": (a, b) => a === b,
			"!==": (a, b) => a !== b,
			"<": (a, b) => a < b,
			">": (a, b) => a > b,
			"<=": (a, b) => a <= b,
			">=": (a, b) => a >= b,
		};
		// Verify that the operation is allowed
		const operationFn = operations[options.operation];
		if (!operationFn) {
			throw new Error("Invalid operation");
		}
		// Perform filtering
		const filterTable = await this.db
			.table(table)
			.filter((item) => operationFn(item[options.field], options.condition));
		await filterTable.each((element) => resultData.push(element));
		return resultData;
	},
	async where_(table, query, options) {
		const myTable = this.db.table(table);
		const whereCond = myTable.where(query);
		switch (options.query) {
			case "first": {
				const result = await whereCond.first();
				return result;
			}
			case "each": {
				const resultData = [];
				await whereCond.each((element) => resultData.push(element));
				return resultData;
			}
			default: {
				console.error("where_ error: invalid option:", options.query);
				reject(`error: invalid option: ${options.query}`);
				break;
			}
		}
	},
	async saveMatchFilter(table, data, key = this.generateRandomKey()) {
		const myTable = this.db.table(table);
		const copyData = {
			...data,
			key,
			date_create: DateUtils.timeStamp(),
		};
		try {
			// Intentar guardar los datos en la tabla
			return await myTable.add(copyData, key);
		} catch (error) {
			console.error("Error al guardar en IndexedDB:", error);
			// Si hay un error, limpiar el caché (IndexedDB) y reintentar
			await this.handleCacheError(table, copyData, key);
		}
	},
	async handleCacheError(table, data, key) {
		console.warn("Limpieza de caché debido a un error en el almacenamiento...");
		// Limpiar el almacenamiento de IndexedDB eliminando la base de datos
		indexedDB.deleteDatabase("openrouteAdmin");
		// Puedes limpiar también otros tipos de almacenamiento, como localStorage
		localStorage.clear();
		localStorage.removeItem("accessToken");
		router.push({path: "/pages/login"}).then((r) => {});
		// Opción: notificar al usuario
		alert(
			"El caché para Serviroutes se ha limpiado debido a un error en el almacenamiento. Intenta acceder nuevamente."
		);
		// Volver a abrir la base de datos y reintentar el guardado
		this.db = this.openIndexedDB(); // Reabrir la base de datos
		const myTable = this.db.table(table);
		// Reintentar guardar los datos después de limpiar el caché
		return myTable.add(data, key);
	},
	openIndexedDB() {
		// Función que abre la base de datos de IndexedDB
		// Implementación de ejemplo para reabrir tu base de datos
		return new Dexie("openrouteAdmin"); // Reemplaza por tu lógica de apertura de DB
	},
	async updateMatchFilter(table, data, key) {
		const myTable = this.db.table(table);
		try {
			// Intentar actualizar los datos en la tabla
			return await myTable.update(key, data);
		} catch (error) {
			console.error("Error al actualizar en IndexedDB:", error);
			// Si hay un error, limpiar el caché y reintentar
			await this.handleCacheError(table, data, key);
		}
	},
};
